import type { ChangeEvent, ReactElement } from 'react';
import { useMemo, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { isEqual, isUndefined } from 'lodash-es';
import { useObservable, useObservableState } from 'observable-hooks';
import { distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import type { Observable } from 'rxjs';
import PropTypes from 'prop-types';
import { defaultPropTypes, useOverrideState, useSharedFormContext } from '../utils';
import FormFieldContainer from './FormFieldContainer';
import Tooltip from '../../../utils/tooltip';
import Select from '../../form/Select';
import { emptyStringToNull, tryToBoolean } from '../../../utils/cast';
import { createFallbackName } from '../../../utils/string';
import { ANY, type ConfigHelper } from '../../pages/measuringPoint/configHelper';

export const PROJECT_OPTION = 'PROJECT_OPTION';

type ChoiceValue = string | boolean | null;
type ChoiceOption = [ChoiceValue, string];
type ChoiceFormValues = Record<string, unknown>;

function isChoiceValue(value: unknown): value is ChoiceValue {
    return value === null || typeof value === 'string' || typeof value === 'boolean';
}

function choiceValueToSelectValue(value: ChoiceValue): string {
    return value === null ? '' : String(value);
}

function getFilteredOptions$(
    name: string,
    options: ChoiceOption[],
    configHelper: ConfigHelper
): Observable<ChoiceOption[]> {
    if (!configHelper.isEnabledForField(name)) {
        return of(options);
    }

    return configHelper.availableFields$.pipe(
        filter((availableFields) => Object.keys(availableFields).includes(name)),
        map((availableFields): ChoiceOption[] => {
            const availableOptions = availableFields[name];

            if (availableOptions === ANY) {
                return options;
            }

            return options.filter(([key, _value]) =>
                availableOptions.some((availableOption) => availableOption === key)
            );
        }),
        distinctUntilChanged<ChoiceOption[]>((previous, current) => isEqual(previous, current))
    );
}

function useFilteredOptionsByConfigHelper(name: string, options: ChoiceOption[]): ChoiceOption[] {
    const { formController } = useSharedFormContext();

    return useObservableState(
        useObservable<ChoiceOption[], [string, ChoiceOption[], ConfigHelper]>(
            (inputs$) =>
                inputs$.pipe(
                    switchMap(([iName, iOptions, iConfigHelper]) =>
                        getFilteredOptions$(iName, iOptions, iConfigHelper)
                    )
                ),
            [name, options, formController.configHelper]
        ),
        options
    );
}

interface ChoiceFieldSettings {
    options?: ChoiceOption[];
    default?: unknown;
}

interface ChoiceFieldOptions {
    name: string;
    settings?: ChoiceFieldSettings;
    required?: boolean;
}

interface ChoiceFieldProps {
    fieldOptions: ChoiceFieldOptions;
    extraChoiceOptions?: ChoiceOption[];
    addOptionWhenMissing?: boolean;
}

function ChoiceField({
    fieldOptions,
    extraChoiceOptions = [],
    addOptionWhenMissing = false,
}: ChoiceFieldProps): ReactElement {
    const { name, settings, required } = fieldOptions;

    const { control } = useFormContext<ChoiceFormValues>();

    const { formController } = useSharedFormContext();
    const parentValues = useObservableState(formController.parentValues$, null) as Record<
        string,
        unknown
    > | null;

    const parentValue = parentValues?.[name];
    const { overridable, overridden } = useOverrideState(name);

    const originalOptions = useFilteredOptionsByConfigHelper(name, settings?.options ?? []);

    const {
        field: { ref, onChange, value: fieldValue, ...fieldProps },
        fieldState: { error },
    } = useController<ChoiceFormValues>({
        name,
        control,
        rules: { required },
        defaultValue: settings?.default,
    });

    const addedOptionsWhenMissing = useRef(new Map<ChoiceValue, string>());

    const options = useMemo(() => {
        const all: ChoiceOption[] = [...originalOptions, ...extraChoiceOptions];

        if (
            addOptionWhenMissing &&
            isChoiceValue(fieldValue) &&
            fieldValue !== null &&
            !all.map(([key]) => key).includes(fieldValue)
        ) {
            // Store the added missing key inside a ref. This ensures it remains
            // available even if you switch to another setting during the edit session.
            addedOptionsWhenMissing.current.set(fieldValue, createFallbackName(String(fieldValue)));
        }

        addedOptionsWhenMissing.current.forEach((displayName, missingKey) => {
            // Here, we add the missing entries to the options. During the
            // lifetime of the page, `all` may be supplemented with extra options
            // from an API request, for example. In that case, we don't want to
            // add the previously stored missing key if it has been added to `all`
            // in the meantime.
            if (!all.some(([existingKey]) => existingKey === missingKey)) {
                all.push([missingKey, displayName]);
            }
        });

        if (overridable && !isUndefined(parentValue) && isChoiceValue(parentValue)) {
            const parentOption = all.find(([key]) => key === parentValue);

            // The active configuration can exclude a project value (for example,
            // when a measuring point enables an integration with fixed settings).
            // Do not offer an inherited value that the form cannot validly select.
            if (parentOption) {
                all.push([PROJECT_OPTION, `${parentOption[1]} - ${gettext('project setting')}`]);
            }
        }

        return all;
    }, [
        extraChoiceOptions,
        originalOptions,
        addOptionWhenMissing,
        fieldValue,
        overridable,
        parentValue,
    ]);

    const selectOptions = useMemo(
        () =>
            options.map(([key, displayName]): [string, string] => [
                choiceValueToSelectValue(key),
                displayName,
            ]),
        [options]
    );

    function transformInput(inputValue: unknown): string {
        if (overridable && !overridden && options.some(([key]) => key === PROJECT_OPTION)) {
            return PROJECT_OPTION;
        }

        return isChoiceValue(inputValue) ? choiceValueToSelectValue(inputValue) : '';
    }

    function transformOutput(e: ChangeEvent<HTMLSelectElement>): unknown {
        const { value: rawValue } = e.target;
        const value = emptyStringToNull(tryToBoolean(rawValue));

        if (overridable) {
            if (value === PROJECT_OPTION && overridden) {
                formController.setOverriddenField(name, false);
                return parentValue;
            }

            if (!overridden) {
                formController.setOverriddenField(name, true, true);
            }
        }

        // Normal behavior.
        return value;
    }

    return (
        <FormFieldContainer fieldOptions={fieldOptions} showOverrideCheckbox={false} inline={true}>
            <Tooltip
                content={gettext('Note: changing this value will override the project settings.')}
                disabled={!overridable || overridden}
            >
                <Select
                    id={`id_${name}`}
                    onChange={(e) => onChange(transformOutput(e))}
                    value={transformInput(fieldValue)}
                    data={selectOptions}
                    highlight={!!error}
                    ref={ref}
                    {...fieldProps}
                />
            </Tooltip>
        </FormFieldContainer>
    );
}

ChoiceField.propTypes = {
    ...defaultPropTypes,
    extraChoiceOptions: PropTypes.array,
    addOptionWhenMissing: PropTypes.bool,
};

export default ChoiceField;
